// ./src/components/features/LostFound/LostFoundItemsList/LostFoundItemsList.tsx
//
// ROADMAP #68 — an event's found items: a table on desktop, cards below (buttons on a tablet, a swipe on a phone). Each
// row: the item's number, what it is (and its line for the public list), where it is in its life (on its way from a
// finder, at the desk, returned, disposed of), where and when it was found, where it is kept, and how long.
//
// The row's DO-SOMETHING actions follow its state — receive what a finder reported, hand back or dispose of what the desk
// holds, undo a handover / disposal — and sit on the OTHER side of a phone's swipe from Edit / Delete (`secondary: true`).
import React, { useCallback, useMemo } from "react";
import { Paper, Stack, Group, Text, Box, ActionIcon, Tooltip, Badge, Skeleton, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { LostFoundItem } from "@/utils/api";
import { useAppEnvironment } from "@/contexts";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG } from "@/utils";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  TableDateTime,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";
import { useEntityCommentsDrawer } from "@/components/features/Comments";

import { lostFoundCategoryIcon, lostFoundCategoryLabel, lostFoundItemStateColor, lostFoundItemStateLabel, lostFoundNumber } from "../lostFoundLabels";

/** A row's do-something actions, by state. */
export type LostFoundItemAction = "receive" | "notReceived" | "return" | "dispose" | "undo";

export interface LostFoundItemsListProps {
  data: LostFoundItem[];
  loading: boolean;
  /** ⚠ REQUIRED — whether the FIRST read has come back (the skeleton belongs to it alone). ▶ check-list-states */
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  /** True when a search or filter is narrowing the list — changes what "empty" means. */
  hasQueryNarrowing: boolean;
  /** The event's service points by id — "Kept at". */
  pointNames: Record<string, string>;
  onEdit?: (item: LostFoundItem) => void;
  onDelete?: (item: LostFoundItem) => void;
  /** The state moves (receive, hand back, dispose, never arrived, undo); absent = none offered. */
  onAction?: (item: LostFoundItem, action: LostFoundItemAction) => void;
  /** First-run empty state's action. */
  onAdd?: () => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  isRemoving: (id?: string) => boolean;
  highlightedId?: string | null;
}

interface ActionDef {
  action: LostFoundItemAction;
  label: string;
  icon: string;
  color: string;
  /** The hook key its spinner runs on (a move the page runs inline). */
  busyKey?: string;
}

/** The do-something actions an item offers in its state. ONE answer for the row buttons and the swipe. */
const actionsFor = (t: TFunction, item: LostFoundItem): ActionDef[] => {
  switch (item.itemState) {
    case "reported":
      return [
        { action: "receive", label: t("lostFound:actions.receive", { defaultValue: "Received at the desk" }), icon: "PackageImport", color: "blue" },
        { action: "notReceived", label: t("lostFound:actions.notReceived", { defaultValue: "Never arrived" }), icon: "X", color: "gray", busyKey: "move:not_received" },
      ];
    case "held":
      return [
        { action: "return", label: t("lostFound:actions.return", { defaultValue: "Hand back" }), icon: "HandStop", color: "green" },
        { action: "dispose", label: t("lostFound:actions.dispose", { defaultValue: "Dispose of" }), icon: "Recycle", color: "orange" },
      ];
    case "not_received":
      return [{ action: "receive", label: t("lostFound:actions.arrived", { defaultValue: "It arrived after all" }), icon: "PackageImport", color: "blue" }];
    case "returned":
    case "disposed":
      return [{ action: "undo", label: t("lostFound:actions.undo", { defaultValue: "Undo — back at the desk" }), icon: "ArrowBackUp", color: "gray", busyKey: "move:held" }];
    default:
      return [];
  }
};

const ItemCell: React.FC<{ item: LostFoundItem }> = ({ item }) => {
  const { t } = useTranslation(["lostFound"]);
  const line = item.publicDescription || item.description;

  return (
    <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
      <Box mt={4} style={{ flexShrink: 0 }}>
        <AppIcon iconName={lostFoundCategoryIcon(item.category)} size={18} />
      </Box>
      <Stack gap={2} style={{ minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          <Text size="md" fw={600} truncate>
            {lostFoundCategoryLabel(t, item.category)}
          </Text>
          {item.isValuable ? (
            <Tooltip label={t("lostFound:list.valuableHint", { defaultValue: "Shown by its category only in public; handed back after an ID check" })} withArrow>
              <Badge size="sm" variant="light" color="grape" style={{ flexShrink: 0 }}>
                {t("lostFound:list.valuable", { defaultValue: "Valuable" })}
              </Badge>
            </Tooltip>
          ) : null}
          {(item.photos?.length ?? 0) > 0 ? (
            <Tooltip label={t("lostFound:list.photos", { defaultValue: "{{count}} photo(s)", count: item.photos?.length ?? 0 })} withArrow>
              <Box style={{ flexShrink: 0, lineHeight: 0 }}>
                <AppIcon iconName="Photo" size={16} />
              </Box>
            </Tooltip>
          ) : null}
        </Group>
        {line ? (
          <Text size="md" c="dimmed" lineClamp={2}>
            {line}
          </Text>
        ) : null}
      </Stack>
    </Group>
  );
};

const StateCell: React.FC<{ item: LostFoundItem }> = ({ item }) => {
  const { t } = useTranslation(["lostFound"]);
  const who = item.itemState === "returned" ? item.returnedTo?.name : item.itemState === "reported" ? item.finder?.name : null;

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Badge size="md" variant="light" color={lostFoundItemStateColor(item.itemState)} style={{ alignSelf: "flex-start" }}>
        {lostFoundItemStateLabel(t, item.itemState)}
      </Badge>
      {/* Made in test mode — hidden once the event is live (like a test guest). Its own line: beside the state it cut the
          state's words short (owner, 2026-10-23). */}
      {item.isTest ? (
        <Badge size="sm" variant="outline" color="orange" style={{ alignSelf: "flex-start" }}>
          {t("lostFound:list.test", { defaultValue: "Test" })}
        </Badge>
      ) : null}
      {who ? (
        <Text size="md" c="dimmed" truncate>
          {item.itemState === "returned" ? t("lostFound:list.to", { defaultValue: "to {{name}}", name: who }) : t("lostFound:list.by", { defaultValue: "by {{name}}", name: who })}
        </Text>
      ) : null}
    </Stack>
  );
};

const KeptCell: React.FC<{ item: LostFoundItem; pointName: string | null }> = ({ item, pointName }) => {
  const { t } = useTranslation(["lostFound"]);
  const place = [pointName, item.storedWhere].filter(Boolean).join(" · ");
  const waiting = item.itemState === "held" || item.itemState === "reported";

  return (
    <Stack gap={2} style={{ minWidth: 0 }}>
      <Text size="md" c={place ? undefined : "dimmed"} lineClamp={1}>
        {place || "—"}
      </Text>
      {waiting && item.keepUntil ? (
        <Text size="md" c="dimmed">
          {t("lostFound:list.keepUntil", { defaultValue: "until {{day}}", day: item.keepUntil })}
        </Text>
      ) : null}
    </Stack>
  );
};

interface RowProps {
  item: LostFoundItem;
  pointName: string | null;
  onEdit?: (item: LostFoundItem) => void;
  onDelete?: (item: LostFoundItem) => void;
  /** The crew's notes on it — a crew-only comment thread. */
  onComments?: (item: LostFoundItem) => void;
  onAction?: (item: LostFoundItem, action: LostFoundItemAction) => void;
  isUpdating: (id?: string, actionKey?: string) => boolean;
  highlighted: boolean;
  isDeleting: boolean;
}

const RowActions: React.FC<RowProps> = ({ item, onEdit, onDelete, onComments, onAction, isUpdating, isDeleting }) => {
  const { t } = useTranslation(["lostFound", "common"]);
  const canEdit = recordHasRight(item, "edit");
  const rowBusy = isUpdating(item.id);
  const moves = onAction && canEdit ? actionsFor(t, item) : [];

  return (
    <Group gap={4} wrap="nowrap" justify="flex-end">
      {moves.map((m) => (
        <Tooltip key={m.action} label={m.label} withArrow>
          <ActionIcon
            variant="subtle"
            color={m.color}
            size="lg"
            loading={m.busyKey ? isUpdating(item.id, m.busyKey) : false}
            disabled={isDeleting || (rowBusy && !(m.busyKey && isUpdating(item.id, m.busyKey)))}
            onClick={() => onAction?.(item, m.action)}
            aria-label={m.label}
          >
            <AppIcon iconName={m.icon} size={18} />
          </ActionIcon>
        </Tooltip>
      ))}
      {onComments ? (
        <Tooltip label={t("lostFound:actions.notes", { defaultValue: "Crew notes" })} withArrow>
          <ActionIcon variant="subtle" color="neutral" size="lg" disabled={isDeleting} onClick={() => onComments(item)} aria-label={t("lostFound:actions.notes", { defaultValue: "Crew notes" })}>
            <AppIcon iconName="MessageCircle" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onEdit && canEdit ? (
        <Tooltip label={t("common:buttons.edit", { defaultValue: "Edit" })} withArrow>
          <ActionIcon variant="subtle" color="neutral" size="lg" disabled={isDeleting || rowBusy} onClick={() => onEdit(item)} aria-label={t("common:buttons.edit", { defaultValue: "Edit" })}>
            <AppIcon iconName="Pencil" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
      {onDelete && recordHasRight(item, "remove") ? (
        <Tooltip label={t("common:buttons.delete", { defaultValue: "Delete" })} withArrow>
          <ActionIcon
            variant="subtle"
            color="error"
            size="lg"
            loading={isDeleting}
            disabled={rowBusy}
            onClick={() => onDelete(item)}
            aria-label={t("common:buttons.delete", { defaultValue: "Delete" })}
          >
            <AppIcon iconName="Trash" size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
};

const ItemTableRow: React.FC<RowProps> = (p) => (
  <Table.Tr
    style={{
      opacity: p.isDeleting ? 0.5 : 1,
      backgroundColor: p.highlighted ? HIGHLIGHT_ROW_BG : undefined,
      transition: "background-color 150ms ease, opacity 150ms ease",
    }}
  >
    {/* The number alone — the column says "No." (owner, 2026-10-23: the narrower the first column, the more the rest shows). */}
    <Table.Td style={{ width: 56 }}>
      <Text size="md" c="dimmed">
        {p.item.sequenceNo ?? ""}
      </Text>
    </Table.Td>
    <Table.Td>
      <ItemCell item={p.item} />
    </Table.Td>
    <Table.Td style={{ width: 190 }}>
      <StateCell item={p.item} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <TableDateTime value={p.item.foundAt ?? null} />
    </Table.Td>
    <Table.Td style={{ width: 200 }}>
      <KeptCell item={p.item} pointName={p.pointName} />
    </Table.Td>
    <Table.Td style={{ width: 190 }}>
      <RowActions {...p} />
    </Table.Td>
  </Table.Tr>
);

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const ItemCardComponent: React.FC<CardProps> = (p) => {
  const { t } = useTranslation(["lostFound", "common"]);
  const { item } = p;

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    const canEdit = recordHasRight(item, "edit");

    if (p.onAction && canEdit) {
      for (const m of actionsFor(t, item)) {
        actions.push({
          key: m.action,
          icon: m.icon,
          label: m.label,
          color: m.color,
          onClick: () => p.onAction?.(item, m.action),
          loading: m.busyKey ? p.isUpdating(item.id, m.busyKey) : false,
          disabled: p.isDeleting,
          secondary: true,
        });
      }
    }

    if (p.onComments) {
      actions.push({ key: "notes", icon: "MessageCircle", label: t("lostFound:actions.notesShort", { defaultValue: "Notes" }), color: "gray", onClick: () => p.onComments?.(item), disabled: p.isDeleting, secondary: true });
    }

    if (p.onEdit && canEdit) {
      actions.push({ key: "edit", icon: "Edit", label: t("common:buttons.edit", { defaultValue: "Edit" }), color: "blue", onClick: () => p.onEdit?.(item), disabled: p.isDeleting });
    }

    if (p.onDelete && recordHasRight(item, "remove")) {
      actions.push({ key: "delete", icon: "Trash", label: t("common:buttons.delete", { defaultValue: "Delete" }), color: "red", onClick: () => p.onDelete?.(item), loading: p.isDeleting, disabled: p.isDeleting });
    }

    return actions;
  }, [p, item, t]);

  const cardBody = (showActions: boolean) => (
    <Paper p="md" withBorder radius="md" opacity={p.isDeleting ? 0.5 : 1} style={{ transition: "opacity 150ms ease", ...(p.highlighted ? HIGHLIGHT_STYLE : {}) }}>
      <Group gap="md" wrap="nowrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" justify="space-between" align="flex-start">
            <ItemCell item={item} />
            <Text size="md" c="dimmed" style={{ flexShrink: 0 }}>
              {lostFoundNumber("Item", item.sequenceNo)}
            </Text>
          </Group>
          <Box mt={8}>
            <StateCell item={item} />
          </Box>
          <Box mt={6}>
            <KeptCell item={item} pointName={p.pointName} />
          </Box>
        </Box>
        {showActions && <RowActions {...p} />}
      </Group>
    </Paper>
  );

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={p.forceClose}
            onForceCloseComplete={p.onForceCloseComplete}
            onSwipeStateChange={p.onSwipeStateChange}
            disabled={p.isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        ) : (
          cardBody(false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true)}</Box>
    </>
  );
};

const ItemCard = React.memo(ItemCardComponent);

const LostFoundItemsListComponent: React.FC<LostFoundItemsListProps> = ({
  data,
  loading,
  isLoaded,
  loadingMore,
  hasMore,
  onLoadMore,
  hasQueryNarrowing,
  pointNames,
  onEdit,
  onDelete,
  onAction,
  onAdd,
  isUpdating,
  isRemoving,
  highlightedId,
}) => {
  const { t } = useTranslation(["lostFound", "common"]);
  const { isDesktop } = useAppEnvironment();
  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  // The crew's notes on a row — a crew-only comment thread (the public side refuses this kind). Mounted once, below.
  const comments = useEntityCommentsDrawer();
  const openComments = useCallback(
    (item: LostFoundItem) => {
      closeAllCards();
      comments.open({ eventId: item.eventId ?? "", entityKind: "lost_found_item", entityId: item.id ?? "", title: lostFoundNumber("Item", item.sequenceNo) });
    },
    [comments, closeAllCards]
  );
  // The tablet card's row buttons, one per handler (▶ check-card-skeletons).
  const cardActions = countCardActions(onEdit, onDelete, openComments, onAction);

  const handleEdit = useCallback(
    (item: LostFoundItem) => {
      closeAllCards();
      onEdit?.(item);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (item: LostFoundItem) => {
      closeAllCards();
      onDelete?.(item);
    },
    [onDelete, closeAllCards]
  );

  const handleAction = useCallback(
    (item: LostFoundItem, action: LostFoundItemAction) => {
      closeAllCards();
      onAction?.(item, action);
    },
    [onAction, closeAllCards]
  );

  const header = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th style={{ width: 56 }}>{t("lostFound:list.columns.number", { defaultValue: "No." })}</Table.Th>
        <Table.Th>{t("lostFound:list.columns.item", { defaultValue: "Item" })}</Table.Th>
        <Table.Th style={{ width: 190 }}>{t("lostFound:list.columns.state", { defaultValue: "Where it is" })}</Table.Th>
        <Table.Th style={{ width: 160 }}>{t("lostFound:list.columns.found", { defaultValue: "Found" })}</Table.Th>
        <Table.Th style={{ width: 200 }}>{t("lostFound:list.columns.kept", { defaultValue: "Kept at" })}</Table.Th>
        <Table.Th style={{ width: 190 }} />
      </Table.Tr>
    </Table.Thead>
  );

  // ⚠ The FIRST read only. ▶ npm run check-list-states
  if (!isLoaded) {
    return (
      <Stack gap="xs" aria-busy="true">
        <Box visibleFrom="md">
          <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              {header}
              <Table.Tbody>
                {[0, 1, 2, 3, 4].map((i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={16} width={50} />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={6}>
                        <Skeleton height={16} width={140} />
                        <Skeleton height={14} width="70%" />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={22} width={100} radius="xl" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width={90} />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={16} width="70%" />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap" justify="flex-end">
                        <Skeleton circle height={34} width={34} />
                        <Skeleton circle height={34} width={34} />
                        <Skeleton circle height={34} width={34} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        </Box>
        {[0, 1, 2].map((i) => (
          // The phone card: what it is (+ its line) and the number, then where it is, then where it is kept.
          <Paper key={`p-${i}`} withBorder radius="md" p="md" hiddenFrom="sm">
            <CardSkeletonLines count={3} gap={8} />
          </Paper>
        ))}
        {[0, 1, 2].map((i) => (
          <Paper key={`t-${i}`} withBorder radius="md" p="md" visibleFrom="sm" hiddenFrom="md">
            <Group gap="md" wrap="nowrap" justify="space-between">
              <CardSkeletonLines count={3} gap={8} />
              <CardSkeletonActions count={cardActions} />
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  }

  if (data.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("lostFound:list.noMatch", { defaultValue: "No items match your search or filters." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("lostFound:list.emptyTitle", { defaultValue: "Nothing found yet" })}
          description={t("lostFound:list.emptyBody", {
            defaultValue:
              "Log what the crew find — a photo, what it is, where it was. Guests can tell you what they found on the event site, and you confirm it here when they bring it to the desk.",
          })}
          iconName="Backpack"
          actionLabel={onAdd ? t("lostFound:actions.log", { defaultValue: "Log a found item" }) : undefined}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (item: LostFoundItem): RowProps => ({
    item,
    pointName: item.heldServicePointId ? (pointNames[item.heldServicePointId] ?? null) : null,
    onEdit: onEdit ? handleEdit : undefined,
    onDelete: onDelete ? handleDelete : undefined,
    onComments: openComments,
    onAction: onAction ? handleAction : undefined,
    isUpdating,
    highlighted: item.id === highlightedId,
    isDeleting: isRemoving(item.id),
  });

  const loadMore = <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />;

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            {header}
            <Table.Tbody>
              {data.map((item) => (
                <ItemTableRow key={item.id} {...rowProps(item)} />
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Stack gap="sm">
        {data.map((item) => (
          <ItemCard
            key={item.id}
            {...rowProps(item)}
            forceClose={shouldForceClose(item.id || "")}
            onForceCloseComplete={() => handleForceCloseComplete(item.id || "")}
            onSwipeStateChange={(isOpen) => isOpen && openCard(item.id || "")}
          />
        ))}
      </Stack>
      {loadMore}
      {comments.drawer}
    </Stack>
  );
};

export const LostFoundItemsList = React.memo(LostFoundItemsListComponent);
